import queryString from 'query-string';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import RefreshableContent from 'shared/components/RefreshableContent';
import { APP_URL, LIVE_EVENTS_FETCHING_INTERVAL, specialEventsCategoryId, specialLeaguesParentId } from 'shared/constants/common';
import { CategoryDataInterface, SportsType, State } from 'shared/interface';
import { BannerData, HotBannerData } from 'shared/types/cms';
import { isMobile } from 'shared/util/utility';
import { fetchBanners, fetchBestsellerData, fetchHotBanners, fetchLiveBettingPopularData } from '.';
import { HomeContent } from './components/HomeContent';

import { LiveBetting, SportsApi } from 'api';
import { EsSportEventData } from 'features/esSport/interface/interface';

import { Banner } from 'features/Banner';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import usePolicyData from 'shared/hooks/usePolicyData';
import TopUpAccount from '../topUpAccount';

let bannerFetchingInterval: NodeJS.Timeout | undefined;
let popularLiveEventsFetchingInterval: NodeJS.Timeout | undefined;

export const Home = memo(() => {
	const bestSellerData = useSelector<State, any[]>((state) => state.home.bestSellerData);
	const hotBannerData = useSelector<State, HotBannerData[]>((state) => state.home.hotBannerData);
	const bannerData = useSelector<State, BannerData[]>((state) => state.home.bannerData);
	const [filteredSubCategories, setFilteredSubCategories] = useState<CategoryDataInterface[]>([]);
	const [currentSubCategory, setCurrentSubCategory] = useState<number | null>(null);
	const [popularLiveBettingDataState, setPopularLiveBettingDataState] = useState<EsSportEventData[]>([]);
	const popularLiveBettingData = useSelector<State, EsSportEventData[]>((state) => state.home.popularLiveBettingData);
	const policyData = usePolicyData('HOME');

	bestSellerData.map((sport) => {
		const specialLeagues: any = [];
		const normalLeagues: any = [];
		sport.events.forEach((event: any) => {
			if (specialEventsCategoryId.includes(event.categoryId)) {
				specialLeagues.push(event);
			} else if (event.sportId === 5 && specialLeaguesParentId.includes(event.parentId)) {
				// tennis(sportId: 5) events should also be sorted by parentId
				specialLeagues.push(event);
			} else {
				normalLeagues.push(event);
			}
		});
		sport.events = [...specialLeagues, ...normalLeagues];
		return sport;
	});

	const [sportsData, setSportsData] = useState<any>([]);
	const [sportsType, setSportsType] = useState<any[]>([]);
	const [currentSport, setCurrentSport] = useState<any>(null);
	const [liveCurrentSport, setLiveCurrentSport] = useState<any>(null);
	const isLoggedIn = useIsLoggedIn();

	const [isData, setIsData] = useState(false);

	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();

	const queryParams = queryString.parse(location.search);
	const { type } = queryParams;
	const isLiveBanners = hotBannerData.some((banner) => banner.eventGameId && banner.eventGame.isLive);
	const hotPrematchBanners = hotBannerData.filter((banner) => !banner?.eventGame?.isLive);
	const hotLiveEvents = hotBannerData.filter((banner) => banner?.eventGame?.isLive);
	const hotTab = { sportName: 'Hot', sportId: 0 };
	const metaDescription = policyData.metaDescription
		? policyData.metaDescription
		: 'Zakłady bukmacherskie BETFAN. Polski, legalny bukmacher online. Otwórz konto i sprawdź bonus - aż 200 % od pierwszej wpłaty aż do 400 zł!';

	useEffect(() => {
		dispatch(fetchHotBanners(true));
		dispatch(fetchBanners(!isLoggedIn));
		if (isLiveBanners) {
			bannerFetchingInterval = setInterval(() => {
				dispatch(fetchHotBanners(true));
			}, LIVE_EVENTS_FETCHING_INTERVAL);
		}

		return () => {
			clearInterval(bannerFetchingInterval);
		};
	}, [isLoggedIn, dispatch, isLiveBanners]);

	useEffect(() => {
		dispatch(fetchBestsellerData());
	}, [dispatch]);

	const isLive = localStorage.getItem('liveToggle') === 'true';

	const handleSubCategoryClick = async (categoryId: number) => {
		const selectedCategory = currentSubCategory === categoryId;

		if (isLive) {
			const response = await LiveBetting.getLiveBettingPopular(categoryId);
			const selectedLiveSubcategory = response.data.data.find((category: EsSportEventData) => category.categoryId === categoryId);
			const filteredLiveEvents = selectedLiveSubcategory.sportId === currentSport.sportId;
			setPopularLiveBettingDataState(response.data.data);
			setSportsData({ ...selectedLiveSubcategory, events: filteredLiveEvents });
			setCurrentSubCategory(selectedLiveSubcategory?.categoryId);
		} else {
			const response = await SportsApi.getSportDataByCategoryId(categoryId, undefined);
			const selectedSubcategory = response.data.data.categories.find((category: CategoryDataInterface) => category.categoryId === categoryId);
			const filteredEvents = (selectedSubcategory?.events).filter((event: CategoryDataInterface) => event.sportId === currentSport.sportId);
			setSportsData({ ...selectedSubcategory, events: filteredEvents });
			setCurrentSubCategory(selectedSubcategory?.categoryId);
		}

		if (selectedCategory) {
			resetSubCategory();
		}
	};

	const resetSubCategory = () => {
		const bestSellerForCurrentSport = bestSellerData.find((item) => item.sportId === currentSport.sportId);
		const livePopulars = popularLiveBettingData.filter((item) => item?.sportId === liveCurrentSport?.sportId);
		setCurrentSubCategory(null);
		setSportsData(bestSellerForCurrentSport);
		setPopularLiveBettingDataState(livePopulars);
	};

	useEffect(() => {
		dispatch(fetchLiveBettingPopularData());

		popularLiveEventsFetchingInterval = setInterval(() => {
			dispatch(fetchLiveBettingPopularData(true));
		}, LIVE_EVENTS_FETCHING_INTERVAL);

		return () => {
			clearInterval(popularLiveEventsFetchingInterval);
		};
	}, [dispatch]);

	const getAllEvents = (sports: any[]) => {
		return sports.reduce((allEvents, sport) => allEvents.concat(sport.events), []);
	};

	useEffect(() => {
		const newSportsType = bestSellerData.map((item: any) => {
			return { sportName: item.sportName, sportId: item.sportId };
		});

		let newCurrentSport;
		let newSportsData;

		if (!hotPrematchBanners.length) {
			newCurrentSport = {
				sportName: bestSellerData[0]?.sportName,
				sportId: bestSellerData[0]?.sportId,
			};
			newSportsData = bestSellerData[0];
		} else {
			newCurrentSport = hotTab;
			newSportsData = { hotTab, events: getAllEvents(bannerData) };
		}

		const modifiedSportsType = hotPrematchBanners.length > 0 ? [newCurrentSport, ...newSportsType] : [...newSportsType];
		setSportsData(newSportsData);
		setSportsType(modifiedSportsType);
		setCurrentSport(newCurrentSport);
		setIsData(true);
	}, [bestSellerData, bestSellerData.length, hotPrematchBanners.length]);

	const checkForSubRoutes = () => {
		const pathname = location.pathname;

		// Check reset password request
		if (type === 'new-password') {
			// const { customerId, code } =
			// localStorage.setItem('resetPassData', JSON.stringify({ customerId, code }));
			history.push(APP_URL.resetPassword);
		}

		// login route
		if (pathname === APP_URL.login || pathname === `${APP_URL.login}/`) {
			if (isLoggedIn) history.push(APP_URL.home);
			else history.push(APP_URL.login, window.location.pathname);
		}

		// register route
		if (pathname === APP_URL.register || pathname === `${APP_URL.register}/`) {
			if (isLoggedIn) history.push(APP_URL.home);
			else history.push(APP_URL.register);
		}

		if (pathname === APP_URL.registrationForm || pathname === `${APP_URL.registrationForm}/`) {
			if (isLoggedIn) {
				history.push(APP_URL.home);
			} else {
				history.push(APP_URL.registrationForm);
			}
		}

		if (pathname.startsWith(APP_URL.registrationMojeId)) {
			if (isLoggedIn) {
				history.push(APP_URL.home);
			}
		}
	};

	const onSportChange = (data: SportsType) => {
		console.log('sport change');
		document.getElementById('tabAnimationWrapper')?.classList.add('slideFromTop');

		const allSports = data.sportName === 'Hot';

		const newSportsData = bestSellerData.find((item) => item.sportId === data.sportId);

		setCurrentSport(allSports ? hotTab : data);

		if (hotTab) {
			setSportsData({ hotTab, events: getAllEvents(hotBannerData) });
		} else {
			setSportsData(newSportsData);
		}
		setSportsData(newSportsData);
		setFilteredSubCategories([]);
		setCurrentSubCategory(null);

		if (allSports) {
			setFilteredSubCategories([]);
		}
	};

	const pullRefresh = () => {
		dispatch(fetchBestsellerData());
		dispatch(fetchHotBanners());

		const newSportsData = bestSellerData.length > 0 ? bestSellerData[0] : [];
		const newSportsType = bestSellerData.map((item: any) => {
			return { sportName: item.sportName, sportId: item.sportId };
		});

		const newCurrentSport = {
			sportName: bestSellerData.length > 0 ? bestSellerData[0].sportName : '',
			sportId: bestSellerData.length > 0 ? bestSellerData[0].sportId : '',
		};

		setSportsData(newSportsData);
		setSportsType(newSportsType);
		setCurrentSport(newCurrentSport);
		setIsData(true);

		return new Promise((resolve) => {
			setTimeout(resolve, 200);
		});
	};

	return (
		<>
			<HelmetMetaTags title="Zakłady bukmacherskie BETFAN - Legalny bukmacher online" description={metaDescription} ogtype="website" />
			{isMobile() && (
				<TopUpAccount
					location={'mobile-homepage'}
					className="sticky top-[calc(60px+env(safe-area-inset-top))] overflow-hidden z-[101] -mt-[9px] -mr-[8px] mb-[9px] -ml-[8px] px-2"
				/>
			)}
			<RefreshableContent onRefresh={pullRefresh}>
				<Banner />
				<HomeContent
					sportsType={sportsType}
					onSportChange={onSportChange}
					sportsData={sportsData}
					currentSport={currentSport}
					isData={isData}
					checkForSubRoutes={checkForSubRoutes}
					filteredSubCategories={filteredSubCategories}
					setFilteredSubCategories={setFilteredSubCategories}
					handleSubCategoryClick={(categoryId: number) => handleSubCategoryClick(categoryId)}
					currentSubCategory={currentSubCategory}
					setPopularLiveBettingDataState={setPopularLiveBettingDataState}
					popularLiveBettingDataState={popularLiveBettingDataState}
					setCurrentSubCategory={setCurrentSubCategory}
					setLiveCurrentSport={setLiveCurrentSport}
					liveCurrentSport={liveCurrentSport}
					hotLiveEvents={hotLiveEvents}
					hotPrematchEvents={hotPrematchBanners}
					isLiveBanners={isLiveBanners}
					hotTab={hotTab}
				/>
			</RefreshableContent>
		</>
	);
});
